import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getChainDisplayNameForRequest, getExplorerTransactionUrlForRequest, getLogicalChainKey, CHAINS } from '../config/chains';
import { getRequestApplication, getApplicationDisplayName } from '../config/applications';
import { roundDecimal } from '../utils/numbers';
import { normalizeAddr, formatAddress } from '../utils/address';
import DecryptionModal from './DecryptionModal';
import AccumulationModal from './AccumulationModal';
import './TransactionList.css';

const DEFAULT_PAGE_SIZE = 10;
const MAX_AUTO_PAGE_SIZE = 50;
const FIXED_PAGE_SIZES = [10, 25, 50];

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items = [1];
  const rangeStart = Math.max(2, currentPage - 1);
  const rangeEnd = Math.min(totalPages - 1, currentPage + 1);

  if (rangeStart > 2) items.push('start-ellipsis');
  for (let page = rangeStart; page <= rangeEnd; page += 1) {
    items.push(page);
  }
  if (rangeEnd < totalPages - 1) items.push('end-ellipsis');

  items.push(totalPages);
  return items;
};

const CopyableValue = ({ value, display }) => {
  const [copied, setCopied] = useState(false);

  if (!value || value === 'N/A') {
    return <span>{display || 'N/A'}</span>;
  }

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      // Clipboard may be unavailable in some environments.
    }
  };

  return (
    <span className="ui-copyable" title={value}>
      <span>{display || formatAddress(value)}</span>
      <button
        type="button"
        className="ui-copy-btn"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : `Copy ${value}`}
      >
        {copied ? '✓' : '⧉'}
      </button>
    </span>
  );
};

const TransactionList = ({
  userAddress,
  transactions,
  loading,
  auditorData,
  filters = {},
  onRemoveFilter,
}) => {
  const [selectedTx, setSelectedTx] = useState(null);
  const [showAccumulationModal, setShowAccumulationModal] = useState(false);
  const [decryptedAmounts, setDecryptedAmounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSizeMode, setPageSizeMode] = useState('fit');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const tableContainerRef = useRef(null);
  const previousPageSizeRef = useRef(DEFAULT_PAGE_SIZE);
  const filterResetRef = useRef(false);
  const resizeTimerRef = useRef(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getChainNameForTx = (tx) => getChainDisplayNameForRequest(tx);

  const getExplorerTxUrlForTx = (tx) => getExplorerTransactionUrlForRequest(tx);

  const handleDecrypt = (transaction) => {
    setSelectedTx(transaction);
  };

  const handleDecryptionComplete = (txId, amount) => {
    setDecryptedAmounts((prev) => ({
      ...prev,
      [txId]: amount,
    }));
    setSelectedTx(null);
  };

  const handleBulkDecryptionComplete = (amountsMap) => {
    setDecryptedAmounts((prev) => ({
      ...prev,
      ...amountsMap,
    }));
    setShowAccumulationModal(false);
  };

  const escapeCsvCell = (value) => {
    if (value == null || value === '') return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const filteredTransactions = useMemo(() => {
    if (!userAddress) return transactions;

    const target = normalizeAddr(userAddress);

    return transactions.filter((tx) => {
      const type = tx.type?.toLowerCase() || '';
      const sender = normalizeAddr(tx.userAddress || tx.details?.sender);
      const recipient = normalizeAddr(tx.details?.recipient);

      const isReceiverRecord = type === 'received' || type === 'receive';
      const isSenderRecord = type === 'send' || type === 'transfer' || type === 'sent';

      if (isReceiverRecord) return recipient === target;
      if (isSenderRecord) return sender === target;
      return sender === target || recipient === target;
    });
  }, [transactions, userAddress]);

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / pageSize) || 1);

  useEffect(() => {
    filterResetRef.current = true;
    setCurrentPage(1);
  }, [transactions, userAddress]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (pageSizeMode !== 'fit') return undefined;
    if (filteredTransactions.length === 0) return undefined;

    const container = tableContainerRef.current;
    if (!container) return undefined;

    const updatePageSize = () => {
      const parent = container.parentElement;
      const parentStyle = parent ? window.getComputedStyle(parent) : null;
      const constrained =
        parent &&
        parentStyle &&
        parentStyle.overflow !== 'visible' &&
        parent.clientHeight > 0 &&
        parent.clientHeight < window.innerHeight;

      if (!constrained && window.innerWidth <= 1024) {
        setPageSize((size) => (size === DEFAULT_PAGE_SIZE ? size : DEFAULT_PAGE_SIZE));
        return;
      }

      const headerHeight =
        container.querySelector('thead')?.getBoundingClientRect().height || 35;
      const rowHeight =
        container.querySelector('tbody tr')?.getBoundingClientRect().height || 44;
      const availableHeight = container.clientHeight - headerHeight;

      if (availableHeight <= 0 || rowHeight <= 0) return;

      const nextPageSize = Math.min(
        MAX_AUTO_PAGE_SIZE,
        Math.max(1, Math.floor(availableHeight / rowHeight))
      );
      setPageSize((size) => (size === nextPageSize ? size : nextPageSize));
    };

    const scheduleUpdate = () => {
      window.clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(updatePageSize, 120);
    };

    updatePageSize();

    if (typeof window.ResizeObserver === 'function') {
      const observer = new window.ResizeObserver(scheduleUpdate);
      observer.observe(container);
      return () => {
        observer.disconnect();
        window.clearTimeout(resizeTimerRef.current);
      };
    }

    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('resize', scheduleUpdate);
      window.clearTimeout(resizeTimerRef.current);
    };
  }, [pageSizeMode, filteredTransactions.length, loading]);

  useEffect(() => {
    const previousPageSize = previousPageSizeRef.current;
    if (previousPageSize !== pageSize) {
      if (!filterResetRef.current) {
        setCurrentPage((page) => Math.floor(((page - 1) * previousPageSize) / pageSize) + 1);
      }
      previousPageSizeRef.current = pageSize;
      filterResetRef.current = false;
    }
  }, [pageSize]);

  const pagedTransactions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(start, start + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  const pageRangeLabel = useMemo(() => {
    if (filteredTransactions.length === 0) return '0 transactions';
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, filteredTransactions.length);
    return `Showing ${start}–${end} of ${filteredTransactions.length}`;
  }, [filteredTransactions.length, currentPage, pageSize]);

  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    tableContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeModeChange = (mode) => {
    setPageSizeMode(mode);
    if (mode !== 'fit') {
      setPageSize(Number(mode));
    }
  };

  const escapeCsvCellValue = escapeCsvCell;

  const handleDownloadAccumulatedCsv = () => {
    const txHeaders = ['Chain', 'Token', 'Date', 'Type', 'From', 'To', 'Tx Hash', 'Amount'];
    const rows = [];
    for (const acc of accumulatedByChainToken) {
      const groupTxs = filteredTransactions.filter(
        (tx) =>
          getLogicalChainKey(tx) === acc.logicalChainKey &&
          (tx.details?.token || 'N/A') === acc.token &&
          decryptedAmounts[tx.id] !== undefined
      );
      for (const tx of groupTxs) {
        const sender =
          tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
            ? (tx.details?.sender || 'N/A')
            : (tx.userAddress || tx.details?.sender || 'N/A');
        const receiver =
          tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
            ? (tx.userAddress || tx.details?.recipient || 'N/A')
            : (tx.details?.recipient || 'N/A');
        const viewingUser = userAddress ? normalizeAddr(userAddress) : '';
        const fromNorm = normalizeAddr(sender);
        const toNorm = normalizeAddr(receiver);
        let role = tx.role || 'sender';
        if (viewingUser) {
          if (fromNorm === viewingUser) role = 'sender';
          else if (toNorm === viewingUser) role = 'recipient';
        }
        rows.push(
          [
            acc.chainName,
            acc.token,
            formatDate(tx.createdAt),
            role === 'sender' ? 'Sent' : 'Received',
            sender,
            receiver,
            tx.transactionHash || 'N/A',
            decryptedAmounts[tx.id],
          ]
            .map(escapeCsvCellValue)
            .join(',')
        );
      }
      rows.push(['', '', 'Summary', '', '', '', 'Received', `${acc.received} ${acc.token}`].map(escapeCsvCellValue).join(','));
      rows.push(['', '', '', '', '', '', 'Sent', `${acc.sent} ${acc.token}`].map(escapeCsvCellValue).join(','));
      rows.push(['', '', '', '', '', '', 'Net', `${acc.net} ${acc.token}`].map(escapeCsvCellValue).join(','));
      rows.push('');
    }
    const csv = [txHeaders.map(escapeCsvCellValue).join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aggregated-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    const headers = ['Date', 'Type', 'From', 'To', 'Chain', 'Token', 'Application', 'Tx Hash', 'Amount'];
    const rows = filteredTransactions.map((tx) => {
      const sender =
        tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
          ? (tx.details?.sender || 'N/A')
          : (tx.userAddress || tx.details?.sender || 'N/A');
      const receiver =
        tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
          ? (tx.userAddress || tx.details?.recipient || 'N/A')
          : (tx.details?.recipient || 'N/A');
      const viewingUser = userAddress ? normalizeAddr(userAddress) : '';
      const fromNorm = normalizeAddr(sender);
      const toNorm = normalizeAddr(receiver);
      let role = tx.role || 'sender';
      if (viewingUser) {
        if (fromNorm && fromNorm === viewingUser) role = 'sender';
        else if (toNorm && toNorm === viewingUser) role = 'recipient';
      }
      return [
        formatDate(tx.createdAt),
        role === 'sender' ? 'Sent' : 'Received',
        sender,
        receiver,
        getChainNameForTx(tx),
        tx.details?.token || 'N/A',
        getApplicationDisplayName(getRequestApplication(tx)),
        tx.transactionHash || 'N/A',
        decryptedAmounts[tx.id] !== undefined ? decryptedAmounts[tx.id] : '(encrypted)',
      ]
        .map(escapeCsvCellValue)
        .join(',');
    });
    const csv = [headers.map(escapeCsvCellValue).join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseAmountValue = (displayAmount) => {
    if (displayAmount == null || typeof displayAmount !== 'string') return NaN;
    const trimmed = displayAmount.trim();
    const firstSpace = trimmed.indexOf(' ');
    const numStr = firstSpace >= 0 ? trimmed.slice(0, firstSpace) : trimmed;
    const value = parseFloat(numStr);
    return Number.isFinite(value) ? value : NaN;
  };

  const accumulatedByChainToken = useMemo(() => {
    if (!userAddress || !filteredTransactions.length) return [];
    const viewingUser = normalizeAddr(userAddress);
    const map = new Map();
    for (const tx of filteredTransactions) {
      const raw = decryptedAmounts[tx.id];
      if (raw === undefined) continue;
      const value = parseAmountValue(raw);
      if (Number.isNaN(value)) continue;
      const token = tx.details?.token || 'N/A';
      const logicalChainKey = getLogicalChainKey(tx);
      const key = `${logicalChainKey}-${token}`;
      if (!map.has(key)) {
        map.set(key, {
          logicalChainKey,
          chainName: getChainNameForTx(tx),
          token,
          received: 0,
          sent: 0,
          count: 0,
        });
      }
      const entry = map.get(key);
      const sender =
        tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
          ? (tx.details?.sender || '')
          : (tx.userAddress || tx.details?.sender || '');
      const receiver =
        tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
          ? (tx.userAddress || tx.details?.recipient || '')
          : (tx.details?.recipient || '');
      const fromNorm = normalizeAddr(sender);
      const toNorm = normalizeAddr(receiver);
      if (fromNorm === viewingUser) entry.sent += value;
      else if (toNorm === viewingUser) entry.received += value;
      entry.count += 1;
    }
    return Array.from(map.entries())
      .filter(([, v]) => v.count > 0)
      .map(([key, v]) => ({
        key,
        logicalChainKey: v.logicalChainKey,
        chainName: v.chainName,
        token: v.token,
        received: roundDecimal(v.received),
        sent: roundDecimal(v.sent),
        net: roundDecimal(v.received - v.sent),
        count: v.count,
      }))
      .sort((a, b) => (a.chainName + a.token).localeCompare(b.chainName + b.token));
  }, [userAddress, filteredTransactions, decryptedAmounts]);

  const hasAccumulatedTotals = accumulatedByChainToken.length > 0;
  const auditScope = auditorData?.accessControls || auditorData?.access_controls || [];
  const hasAuditScope = Array.isArray(auditScope) && auditScope.length > 0;

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.userAddress) {
      chips.push({
        key: 'userAddress',
        label: `Account ${formatAddress(filters.userAddress)}`,
      });
    }
    if (filters.chain) {
      chips.push({
        key: 'chain',
        label: `Chain ${CHAINS[filters.chain]?.name || filters.chain}`,
      });
    }
    if (filters.token) {
      chips.push({
        key: 'token',
        label: `Token ${filters.token}`,
      });
    }
    if (filters.application) {
      chips.push({
        key: 'application',
        label: `App ${getApplicationDisplayName(filters.application)}`,
      });
    }
    return chips;
  }, [filters.userAddress, filters.chain, filters.token, filters.application]);

  const showInitialLoading = loading && filteredTransactions.length === 0;

  return (
    <div className={`transaction-list ui-panel${loading ? ' is-loading' : ''}`}>
      <div className="transaction-list-header ui-panel-header">
        <h2>Confidential Transfers</h2>
        <div className="transaction-list-header-actions">
          <span className="transaction-count ui-count-chip">
            {filteredTransactions.length}{' '}
            {filteredTransactions.length === 1 ? 'transaction' : 'transactions'}
          </span>
          {filteredTransactions.length > 0 && (
            <>
              {userAddress && (
                <button
                  type="button"
                  onClick={() => setShowAccumulationModal(true)}
                  className="accumulate-button"
                >
                  All user transactions
                </button>
              )}
              <button type="button" onClick={handleDownloadCsv} className="download-csv-button">
                Download CSV
              </button>
              {hasAccumulatedTotals && (
                <button
                  type="button"
                  onClick={handleDownloadAccumulatedCsv}
                  className="download-csv-button"
                >
                  Download totals
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="active-filters" aria-label="Active filters">
          {activeFilterChips.map((chip) => (
            <span className="ui-filter-chip" key={chip.key}>
              {chip.label}
              {onRemoveFilter && (
                <button
                  type="button"
                  onClick={() => onRemoveFilter(chip.key)}
                  aria-label={`Remove ${chip.label} filter`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {showInitialLoading ? (
        <div className="transaction-table-container" role="status" aria-label="Loading transactions">
          <table className="transaction-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Chain</th>
                <th>Token</th>
                <th>Application</th>
                <th>Tx Hash</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }, (_, index) => (
                <tr key={index}>
                  {Array.from({ length: 9 }, (__, cell) => (
                    <td key={cell}>
                      <span className="ui-skeleton transaction-cell-skeleton" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="empty-state">
          {!hasAuditScope ? (
            <>
              <h3 className="empty-state-title">No audit scope assigned</h3>
              <p>
                Your account has no access rules yet, so no confidential transfers can be shown.
                Ask an administrator to grant you access to the chains, tokens, or accounts you
                need to review.
              </p>
            </>
          ) : userAddress && transactions.length > 0 ? (
            <>
              <h3 className="empty-state-title">No transfers for this account</h3>
              <p>
                This account has no confidential transfers within the scope you can audit. Select a
                different account, or clear the filters to widen the search.
              </p>
              {onRemoveFilter && (
                <button
                  type="button"
                  className="ui-btn ui-btn--secondary"
                  onClick={() => onRemoveFilter('userAddress')}
                >
                  Clear account filter
                </button>
              )}
            </>
          ) : (
            <>
              <h3 className="empty-state-title">No transfers in your audit scope</h3>
              <p>
                Nothing has been recorded for the chains, tokens, and accounts you have access to.
                Adjust the filters, or ask an administrator to extend your access.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="transaction-table-shell">
            {loading && (
              <div className="transaction-loading-overlay" role="status" aria-live="polite">
                <div className="loading-spinner" />
                <span>Updating results…</span>
              </div>
            )}
            <div className="transaction-table-container" ref={tableContainerRef}>
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Chain</th>
                    <th>Token</th>
                    <th>Application</th>
                    <th>Tx Hash</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTransactions.map((tx) => {
                    const isDecrypted = decryptedAmounts[tx.id] !== undefined;
                    const sender =
                      tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
                        ? (tx.details?.sender || 'N/A')
                        : (tx.userAddress || tx.details?.sender || 'N/A');
                    const receiver =
                      tx.type?.toLowerCase() === 'received' || tx.type?.toLowerCase() === 'receive'
                        ? (tx.userAddress || tx.details?.recipient || 'N/A')
                        : (tx.details?.recipient || 'N/A');

                    const viewingUser = userAddress ? normalizeAddr(userAddress) : '';
                    const fromNorm = normalizeAddr(sender);
                    const toNorm = normalizeAddr(receiver);

                    let role = tx.role || 'sender';
                    if (viewingUser) {
                      if (fromNorm && fromNorm === viewingUser) role = 'sender';
                      else if (toNorm && toNorm === viewingUser) role = 'recipient';
                    }

                    const explorerUrl = getExplorerTxUrlForTx(tx);
                    const hasExplorer = explorerUrl && explorerUrl !== '#';

                    return (
                      <tr key={tx.id}>
                        <td>{formatDate(tx.createdAt)}</td>
                        <td>
                          <span className={`role-badge ${role}`}>
                            {role === 'sender' ? 'Sent' : 'Received'}
                          </span>
                        </td>
                        <td className="address-cell">
                          <CopyableValue value={sender} display={formatAddress(sender)} />
                        </td>
                        <td className="address-cell">
                          <CopyableValue value={receiver} display={formatAddress(receiver)} />
                        </td>
                        <td className="chain-cell">{getChainNameForTx(tx)}</td>
                        <td className="token-cell">
                          <span className="token-chip">{tx.details?.token || 'N/A'}</span>
                        </td>
                        <td className="application-cell">
                          <span className="token-chip">
                            {getApplicationDisplayName(getRequestApplication(tx))}
                          </span>
                        </td>
                        <td className="hash-cell">
                          {hasExplorer ? (
                            <a
                              href={explorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tx-link"
                              title={tx.transactionHash || ''}
                            >
                              {formatAddress(tx.transactionHash)}
                            </a>
                          ) : (
                            <CopyableValue
                              value={tx.transactionHash}
                              display={formatAddress(tx.transactionHash)}
                            />
                          )}
                        </td>
                        <td className="amount-cell">
                          {isDecrypted ? (
                            <span className="decrypted-amount">{decryptedAmounts[tx.id]}</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleDecrypt(tx)}
                              className="decrypt-button"
                              aria-label={`Decrypt amount for transaction ${formatAddress(tx.transactionHash)}`}
                            >
                              Decrypt
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <nav className="transaction-pagination" aria-label="Transaction pages">
            <div className="pagination-left">
              <span className="pagination-summary" aria-live="polite">
                {pageRangeLabel}
              </span>
              <label className="page-size-control">
                <span className="visually-hidden">Rows per page</span>
                <select
                  value={pageSizeMode}
                  onChange={(e) => handlePageSizeModeChange(e.target.value)}
                  aria-label="Rows per page"
                >
                  <option value="fit">Fit view</option>
                  {FIXED_PAGE_SIZES.map((size) => (
                    <option key={size} value={String(size)}>
                      {size} / page
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  type="button"
                  className="pagination-btn pagination-direction"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Go to previous page"
                >
                  <span className="pagination-chevron" aria-hidden="true">
                    ‹
                  </span>
                  <span className="pagination-direction-label">Previous</span>
                </button>

                <div className="pagination-pages">
                  {paginationItems.map((item) =>
                    typeof item === 'number' ? (
                      <button
                        type="button"
                        key={item}
                        className={`pagination-btn pagination-page${item === currentPage ? ' active' : ''}`}
                        onClick={() => handlePageChange(item)}
                        aria-label={`Go to page ${item}`}
                        aria-current={item === currentPage ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span className="pagination-ellipsis" key={item} aria-hidden="true">
                        …
                      </span>
                    )
                  )}
                </div>

                <button
                  type="button"
                  className="pagination-btn pagination-direction"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="Go to next page"
                >
                  <span className="pagination-direction-label">Next</span>
                  <span className="pagination-chevron" aria-hidden="true">
                    ›
                  </span>
                </button>
              </div>
            )}
          </nav>
        </>
      )}

      {selectedTx && (
        <DecryptionModal
          transaction={selectedTx}
          userAddress={userAddress}
          auditorData={auditorData}
          onClose={() => setSelectedTx(null)}
          onDecryptionComplete={handleDecryptionComplete}
        />
      )}

      {showAccumulationModal && userAddress && (
        <AccumulationModal
          userAddress={userAddress}
          transactions={filteredTransactions}
          auditorData={auditorData}
          onClose={() => setShowAccumulationModal(false)}
          onBulkDecryptionComplete={handleBulkDecryptionComplete}
        />
      )}
    </div>
  );
};

export default TransactionList;
